import * as React from "react";
import styled, { css } from "styled-components";
import { select } from "@hubper/theme";
import { PoseGroup } from "react-pose";

const WIDTH = 250;
const HEIGHT = 225;
const BORDER_RADIUS = 10;

const hoverStyle = css`
  &:hover {
    box-shadow: ${select.shadow("card2")};
  }
`;
const activeStyle = css`
  &:active {
    box-shadow: ${select.shadow("card1")};
  }
`;

const playlistStyle = css`
  &:before {
    content: " ";
    background-color: #fbfbfb;
    border-radius: ${BORDER_RADIUS}px;
    width: 100%;
    height: 100%;
    box-shadow: 0px 0px 1px 1px rgba(0, 0, 0, 0.15);
    position: absolute;
    top: 18px;
    left: 0px;
    z-index: -1;
    transform: scale(0.9) rotate(1.66deg) translateZ(-2px);
  }
  &:after {
    content: " ";
    background-color: #fbfbfb;
    border-radius: ${BORDER_RADIUS}px;
    width: 100%;
    height: 100%;
    box-shadow: 0px 0px 1px 1px rgba(0, 0, 0, 0.15);
    position: absolute;
    top: 9px;
    left: 0px;
    z-index: -1;
    transform: scale(0.95) rotate(0.8deg) translateZ(-1px);
  }
`;

type BaseProps = {
  disabled?: boolean;
  onClick?: () => void;
  overlay?: boolean;
  draggable?: boolean;
  playlist?: boolean;
};

function getCursor(props: BaseProps) {
  if (props.disabled) {
    return "default";
  }

  if (props.draggable) {
    return "move";
  }

  if (props.onClick) {
    return "pointer";
  }

  return "default";
}

const Base = styled.div<BaseProps>`
  position: relative;
  width: ${WIDTH}px;
  height: ${HEIGHT}px;
  border-radius: ${BORDER_RADIUS}px;
  transition: box-shadow 0.2s;
  background-color: white;
  box-shadow: ${select.shadow("card1")};
  cursor: ${getCursor};

  ${p => !p.disabled && !p.draggable && hoverStyle}
  ${p => !p.disabled && activeStyle}
  ${p => p.playlist && playlistStyle}
`;

type ProgressBarProps = {
  progress: number | null | undefined;
};

const getProgressBackground = (
  progress: ProgressBarProps["progress"],
  color: string
) => {
  if (!progress) {
    return "transparent";
  }

  return `linear-gradient(to right, ${color} 0%, ${color} ${progress}%, transparent ${progress}%, transparent 100%)`;
};

const ProgressBar = styled.div<ProgressBarProps>`
  position: absolute;
  height: 3px;
  bottom: 0;
  left: 3px;
  right: 3px;
  background: ${p =>
    getProgressBackground(p.progress, select.color(["success", 5], p))};
  border-bottom-left-radius: ${BORDER_RADIUS}px;
  border-bottom-right-radius: ${BORDER_RADIUS}px;
`;

type Props = {
  style?: React.CSSProperties;
  className?: string;
  showProgressBar?: boolean;
  progress?: number | null;
  disabled?: boolean;
  onClick?: () => void;
  children?: any;
  overlay?: any;
  showOverlayOnHover?: boolean;
  showOverlay?: boolean;
  draggable?: boolean;
  playlist?: boolean;
  onMouseOverChange?: (state: boolean) => void;
} & React.HTMLAttributes<HTMLDivElement>;

function Tile(
  {
    style,
    className,
    showProgressBar,
    progress,
    disabled,
    onClick,
    children,
    showOverlayOnHover,
    showOverlay,
    overlay,
    draggable,
    playlist,
    onMouseOverChange,
    ...rest
  }: Props,
  ref: any
) {
  const [isOver, setOver] = React.useState(false);

  const handleMouseOver = (state: boolean) => {
    if (onMouseOverChange) {
      onMouseOverChange(state);
    }
    setOver(state);
  };

  return (
    <Base
      ref={ref}
      disabled={disabled}
      onClick={onClick}
      style={style}
      className={`${className || ""} tile`}
      overlay={Boolean(overlay)}
      draggable={draggable}
      onMouseEnter={() => handleMouseOver(true)}
      onMouseLeave={() => handleMouseOver(false)}
      playlist={playlist}
      {...rest}
    >
      {showProgressBar && <ProgressBar progress={progress} />}
      <PoseGroup flipMove={false}>
        {overlay &&
          ((showOverlayOnHover && isOver) ||
            (!showOverlayOnHover && showOverlay)) &&
          React.cloneElement(overlay, { key: "overlay" })}
      </PoseGroup>
      {children}
    </Base>
  );
}

export default React.forwardRef(Tile);
